<template>
  <section
    :class="[$style.palette, { [$style.disabled]: disabled }]"
    :aria-label="t('aria')"
    :aria-disabled="disabled || undefined"
  >
    <ul :class="$style.list">
      <li
        v-for="team in teams"
        :key="team.id"
        :class="[
          $style.item,
          {
            [$style.placed]: isPlaced(team.id),
            [$style.selected]: selectedTeamId === team.id,
            [$style.disabled]: disabled
          }
        ]"
      >
        <button
          type="button"
          :class="$style.btn"
          :disabled="disabled || isPlaced(team.id)"
          :title="team.tag"
          :aria-pressed="selectedTeamId === team.id"
          :aria-disabled="isPlaced(team.id)"
          @click="onTeamClick(team)"
        >
          <span :class="$style.icon">
            <team-icon :team="team" size="m" bare />
          </span>
        </button>
      </li>
    </ul>
  </section>
</template>

<i18n lang="yaml">
ru:
  aria: Палитра команд этапа
</i18n>

<script lang="ts" setup>
import TeamIcon from '../../common/TeamIcon.vue'
import type { PickemTeam } from '../../../types'

defineOptions({ name: 'PickemSwissTeamsPalette' })

const props = defineProps<{
  teams: PickemTeam[]
  placedTeamIds?: ReadonlySet<number>
  // Команда «выбрана» — следующий клик по слоту положит её туда.
  selectedTeamId: number | null
  disabled?: boolean
}>()

const emit = defineEmits<{
  'select-team': [team: PickemTeam]
  'deselect-team': []
}>()

const { t } = useI18n()

const isPlaced = (teamId: number) => props.placedTeamIds?.has(teamId) ?? false

const onTeamClick = (team: PickemTeam) => {
  if (props.disabled || isPlaced(team.id)) return
  if (props.selectedTeamId === team.id) {
    emit('deselect-team')
    return
  }
  emit('select-team', team)
}
</script>

<style lang="scss" module>
.palette {
  width: 100%;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 0.4em;
  width: 100%;

  @include down(md) {
    overflow-x: auto;
    @include hide-scrollbar;
  }
}

.item {
  flex: 1 1 0;
  min-width: 0;

  @include down(md) {
    flex: 0 0 auto;
    width: 4.6em;
  }
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.7em 1.2em;
  border: 1px solid transparent;
  border-radius: 1.2em;
  background-color: color-get(--root-rgb-control-default-bg);
  cursor: pointer;
  transition: all 0.15s ease;

  @include hover-focus {
    &:not(:disabled) {
      background-color: color-get(--root-rgb-control-hover-bg);
    }
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.placed .btn {
  background-color: color-get(--root-rgb-bg-card);
}

.placed .icon {
  opacity: 0.6;
}

.selected:not(.placed) .btn {
  background-color: color-get(--root-rgb-control-hover-bg);
  border-color: rgba(var(--root-rgb-accent-2), 1);
}

.disabled {
  pointer-events: none;
  cursor: not-allowed;
}

.disabled .btn {
  opacity: 0.6;
  cursor: not-allowed;
}

.disabled .placed .btn {
  background-color: color-get(--root-rgb-bg-card, 0.6);
}
</style>
